import React, { useMemo, useState } from 'react';
import './DomainsPerModelSection.scss';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { useDomainsPerModel, ModelDomainsData } from '../../hooks/UseDomainsPerModel';
import { getFirstSeriesPalette } from '../../../../shared/utils/chartTheme';
import { MonitorType } from '../../../../shared/types/monitor/MonitorType';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import DomainsFilter from '../domains-filter/DomainsFilter';

interface DomainsPerModelSectionProps {
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  features: MonitorType[];
  selectedChatType: ChatType | null;
  monitorPromptIds?: number[];
  selectedChatTypes?: ChatType[];
  filterMonitorTypes?: string[];
}

interface ModelColumnProps {
  model: ModelDomainsData;
  selectedDomains: string[];
  domainColorMap: Map<string, string>;
}

const ModelColumn: React.FC<ModelColumnProps> = ({ model, selectedDomains, domainColorMap }) => {
  const { t } = useTranslation();

  const visibleDomains = useMemo(() => {
    const list =
      selectedDomains.length > 0
        ? model.domains.filter((d) => selectedDomains.includes(d.domain))
        : model.domains;
    return list.slice(0, 10);
  }, [model.domains, selectedDomains]);

  const maxCount = useMemo(
    () => (visibleDomains.length > 0 ? Math.max(...visibleDomains.map((d) => d.count)) : 1),
    [visibleDomains]
  );

  return (
    <div className="domains-per-model__column">
      <div className="domains-per-model__col-header">
        <div className="domains-per-model__col-title">
          <span className={`ico--sm ${model.icon}`} />
          <span className="domains-per-model__col-name">{model.name}</span>
        </div>
        <span className="domains-per-model__col-total">
          {model.totalCount} {t('total')}
        </span>
      </div>
      <div key={selectedDomains.join(',')} className="domains-per-model__col-rows">
        {visibleDomains.length === 0 ? (
          <div className="domains-per-model__empty">
            <span className="domains-per-model__empty-text">
              {t('noSourcesForModelYet', { model: model.name })}
            </span>
          </div>
        ) : (
          visibleDomains.map((d) => {
            const barWidth = maxCount > 0 ? Math.round((d.count / maxCount) * 100) : 0;
            const color = domainColorMap.get(d.domain) ?? getFirstSeriesPalette()[0];
            return (
              <div key={d.domain} className="domains-per-model__row">
                <div className="domains-per-model__bar-row">
                  <div className="domains-per-model__bar-track">
                    <div
                      className="domains-per-model__bar-fill"
                      style={{ width: `${barWidth}%`, backgroundColor: color }}
                    />
                  </div>
                  <span className="domains-per-model__count">{d.count}</span>
                </div>
                <div className="domains-per-model__domain-row">
                  <img
                    src={`https://favicon.is/${d.domain}`}
                    width={16}
                    height={16}
                    alt=""
                    className="domains-per-model__favicon"
                  />
                  <span className="domains-per-model__domain-name">{d.domain}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const DomainsPerModelSection: React.FC<DomainsPerModelSectionProps> = ({
  projectId,
  startDate,
  endDate,
  features,
  selectedChatType,
  monitorPromptIds,
  selectedChatTypes,
  filterMonitorTypes,
}) => {
  const { t } = useTranslation();
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);

  const { modelsData, allDomains, isLoading } = useDomainsPerModel({
    projectId,
    startDate,
    endDate,
    features,
    selectedChatType,
    monitorPromptIds,
    selectedChatTypes,
    filterMonitorTypes,
  });

  const domainColorMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    allDomains.forEach((domain, index) => {
      map.set(domain, getFirstSeriesPalette()[index % getFirstSeriesPalette().length]);
    });
    return map;
  }, [allDomains]);

  const hasAnyData = modelsData.some((m) => m.domains.length > 0);

  return (
    <div className="stats__panel domains-per-model">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('domainsPerModel')}
          <CustomTooltip tooltipText="domainsPerModelTooltip" addMargin={false} />
        </div>
        <div className="domains-per-model__filter">
          {selectedChatType && (
            <DomainsFilter
              projectId={projectId}
              chatType={selectedChatType}
              from={startDate!}
              to={endDate!}
              selectedDomains={selectedDomains}
              onChange={setSelectedDomains}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="stats-box">
          <div className="stats-box__content domains-per-model__content">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="domains-per-model__column domains-per-model__column--skeleton" />
            ))}
          </div>
        </div>
      ) : !hasAnyData ? (
        <div className="stats-box stats-box--empty">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : (
        <div className="stats-box">
          <div className="stats-box__content">
            <SimpleBar className="domains-per-model__scroll-box" style={{ maxWidth: '100%' }} autoHide={false}>
              <div className="domains-per-model__content">
                {modelsData.map((model) => (
                    <ModelColumn
                      key={model.chatType}
                      model={model}
                      selectedDomains={selectedDomains}
                      domainColorMap={domainColorMap}
                    />
                  ))}
              </div>
            </SimpleBar>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainsPerModelSection;
